import { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Undo2, Eye, EyeOff, CheckCircle, CircleAlert, Upload, Pencil, Trash2, ChevronLeft } from 'lucide-react';

import NewMemberAccountService from '../../services/member-account-service';
import NewRoleService from '../../services/role-service';

import { ConfirmationModal } from '../../components/ConfirmationModal';
import { datetimeFormatter } from '../../utils/DatetimeFormatter';
import formatToDateTimeLocal from '../../utils/LocalDatetimeFormatter';
import convertLocalToUTC from '../../utils/UTCDatetimeFormatter';
import { useAuth } from '../../contexts/AuthContext';
import { showToast } from '../../components/ToastConfig';
import { STATUS_MESSAGES } from '../../utils/api-handlers/member-account-form';

function MemberAccountForm() {
  const Navigate = useNavigate();
  const { id } = useParams();
  const [mode, setMode] = useState('create');
  const [accountData, setAccountData] = useState(null);
  const [photoModified, setPhotoModified] = useState(false);
  const [roleOptions, setRoleOptions] = useState([]);
  const [paymentStatusOptions, setPaymentStatusOptions] = useState([
    {name: "active", displayName: "Active"},
    {name: "cancel", displayName: "Cancel"}, 
  ]);
  const [subscriptionLevelOptions, setSubscriptionLevelOptions] = useState([
    {name: "Free", displayName: "Free"}, 
    {name: "Pro", displayName: "Pro"}, 
    {name: "Enterprise", displayName: "Enterprise"}
  ]);
  const [showPassword, setShowPassword] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showIsEnabledModal, setShowIsEnabledModal] = useState(false);
  const isInitialLoadRef = useRef(true);
  const previousRoleRef = useRef(null);
  const [isRoleDisabled, setIsRoleDisabled] = useState(false);

  // 大頭照
  const photoInputRef = useRef(null);
  const [localPreview, setLocalPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(0);

  const { logout } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setValue,
    reset
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      'role': 'Member',
      // 'subscriptionLevel': 'Free',
      // 'paymentStatus': 'notSubscribed',
      // 'paymentDate': '',
      'testerSubscriptionEndAt': null,
      'newPKeyConfirm': ''
    }
  });

  const newPKeyValue = watch('newPKey');
  useEffect(() => {
    setValue('newPKeyConfirm', newPKeyValue);
  }, [newPKeyValue]);

  // 上傳照片處理函數
  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // 檢查檔案類型
    if (!file.type.match('image/jpeg') && !file.type.match('image/png')) {
      showToast.error('Image format should be JPG or PNG.');
      return;
    }
    
    // 檢查檔案大小 (2MB = 2 * 1024 * 1024 bytes)
    if (file.size > 2 * 1024 * 1024) {
      showToast.error('Image size should be within 2MB.');
      return;
    }
    
    // 先顯示本地預覽圖，提供立即視覺反饋
    const reader = new FileReader();
    reader.onload = (event) => {
      // 先設置本地預覽用的 base64 圖片數據
      setLocalPreview(event.target.result); 
    };
    reader.readAsDataURL(file);
    
    // 準備 FormData 發送到後端
    const formData = new FormData();
    formData.append('file', file);  // 根據後端 API 的期望參數名進行調整
    
    try {
      // 顯示上傳中狀態
      setUploading(true);
      
      // 發送請求
      const res = await NewMemberAccountService.updateProfilePhoto(formData);
      
      if (res.status === 200) {
        // showToast.success('照片上傳成功');
                
        // 設置表單值，確保使用回傳的完整 URL
        setValue('profilePhoto', res.data);
        
        // 強制重新渲染
        // setPhotoModified(true);
        // setForceUpdate(prev => prev + 1);

        // console.log("accountData: ", accountData)
        // const { profilePhoto, id, ...rest} = accountData;

        // const response = await NewMemberAccountService.update(id, { ...rest, profilePhoto: res?.data });
        // console.log("update res: ", response);
      }
    } catch (error) {
      console.error('照片上傳失敗:', error);
      let errorMsg;
      // 詳細記錄錯誤信息以便調試
      if (error.response.status === 400) {
        errorMsg = STATUS_MESSAGES[error?.response?.data?.errorCode] || "Something went wrong. Please try again later.";
      } else {
        errorMsg = STATUS_MESSAGES[error?.response?.status] || "Something went wrong. Please try again later.";
      }
      showToast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  // 刪除照片處理函數
  const handleDeletePhoto = () => {
    // 顯示確認對話框
    if (window.confirm('確定要刪除照片嗎？')) {
      deletePhoto();
    }
  };

  // 刪除照片的處理函數
  const deletePhoto = async () => {
    // [todo]
    // password, confirm password 沒有通過驗證，導致戳失敗

    try {
      // 獲取當前表單的所有值
      const currentFormData = watch();

      const { name, role, newPKey, newPKeyConfirm  } = currentFormData;
      
      // 將 profilePhoto 設為空字串
      const updatedData = {
        ...currentFormData,
        name: name?.trim() || ' ',
        role: role?.trim() || 'Member',
        // newPKey: newPKey?.trim() || ' ',
        // newPKeyConfirm: newPKeyConfirm?.trim() || ' ',
        profilePhoto: ''
      };
      
      // 先清除本地預覽
      setLocalPreview(null);
      
      // 顯示處理中狀態
      setUploading(true);
      
      // 使用更新會員帳號的 API 發送請求
      const res = await NewMemberAccountService.update(id, updatedData);
      
      if (res.status === 200) {
        // showToast.success('照片已刪除');
        setValue('profilePhoto', '');
        setPhotoModified(true);
        setForceUpdate(prev => prev + 1);
      }
    } catch (error) {
      console.error('照片上傳失敗:', error);
      let errorMsg;
      // 詳細記錄錯誤信息以便調試
      if (error.response.status === 400) {
        errorMsg = STATUS_MESSAGES[error?.response?.data?.errorCode] || "Something went wrong. Please try again later.";
      } else {
        errorMsg = STATUS_MESSAGES[error?.response?.status] || "Something went wrong. Please try again later.";
      }
      showToast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    try {
      const res = await NewMemberAccountService.delete(id);
      showToast.success("Account deleted.");
      setTimeout(() => {
        Navigate("/member/account");
      }, 500);
    } catch (error) {
      console.log("Error when deleting. ", error);
      let errorMsg;
      if (error?.response?.status === 400) {
        errorMsg = STATUS_MESSAGES[error?.response?.data?.errorCode] || "Something went wrong. Please try again later.";
      } else {
        errorMsg = STATUS_MESSAGES[error?.response?.status] || "Something went wrong. Please try again later.";
      }
      showToast.error(errorMsg);
    }
  }

  const handleToggleIsEnabled = () => {
    setShowIsEnabledModal(false);
    setValue('isEnabled', !watch('isEnabled'));
  }

  // 添加這個 useEffect 來確保 forceUpdate 變化時重新獲取最新數據
  useEffect(() => {
    // 如果不是 create 模式且 forceUpdate 被觸發
    if (mode === 'edit' && forceUpdate > 0) {
      const refreshData = async () => {
        try {
          const data = await NewMemberAccountService.getDetails(id);
          
          // 如果回傳的 profilePhoto 值與當前不同，則更新
          if (data.data.profilePhoto !== watch('profilePhoto')) {
            setValue('profilePhoto', data.data.profilePhoto);
          }
        } catch (error) {
          console.error('重新取得資料失敗:', error);
        }
      };
      
      refreshData();
    }
  }, [forceUpdate, id, mode]);

  // 獲取角色列表
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const res = await NewRoleService.searchMember();
        setRoleOptions(res.data?.data);
        // // 如果是 create 模式，確保設定預設值
        // if (mode === 'create') {
        //   setValue('role', 'Member');
        // }
      } catch (error) {
        console.error('Failed to fetch roles:', error);
        if (error?.response?.status === 401 || error?.response?.status === 403) {
          logout();
        } else {
          showToast.error('Failed to fetch role options.');
        }
      }
    };

    fetchRoles();
  }, []);

  // 獲取帳號資料
  useEffect(() => {
    const fetchAccountData = async () => {
      if (!id || id === "new") {
        setMode('create');
        isInitialLoadRef.current = false;
        setIsRoleDisabled(false); // create 模式不禁用
        return;
      }

      try {
        setMode('edit');
        const data = await NewMemberAccountService.getDetails(id);
        setAccountData(data.data);
        
        // 設置表單預設值
        Object.keys(data.data).forEach(key => {
          if (typeof data.data[key] === 'string') {
            if (key === "paymentDate") {
              setValue(key, data.data[key].replaceAll("/", "-"));
            } else if (key === "testerSubscriptionEndAt") {
              const transferredDatetime = formatToDateTimeLocal(data.data[key]);
              setValue(key, transferredDatetime);
            } else {
              setValue(key, data.data[key].trim());
            }
          } else {
            setValue(key, data.data[key]);
          }
        });

        setTimeout(() => {
          // 判斷是否需要禁用 role 欄位
          const shouldDisableRole = data.data.role === 'Member' && 
          data.data.subscriptionLevel && 
          data.data.subscriptionLevel.toLowerCase() !== 'free';
          setIsRoleDisabled(shouldDisableRole);
          isInitialLoadRef.current = false;
          previousRoleRef.current = data.data.role;
        }, 100);
      } catch (error) {
        console.error('Failed to fetch account data:', error);
        if (error?.response?.status === 401 || error?.response?.status === 403) {
          // showToast.error('無權訪問此頁面');
          // Navigate('/account/member');
          // return;
          logout();
        } else if (error?.response?.status === 400 && error?.response?.data?.errorCode === '2000') {
          Navigate('/member/account');
          return;
        } else {
          showToast.error('Failed to fetch account data. Please try again.');
        }
      }
    };

    fetchAccountData();
  }, [id, setValue, Navigate]);

  useEffect(() => {
    // 當 profilePhoto 有改變時印出其值
    const photoValue = watch('profilePhoto');
  }, [watch('profilePhoto'), photoModified]);

  const onSubmit = async (data) => {
    // // 如果有輸入密碼，檢查是否與確認密碼相符
    // if (data.newPKey && data.newPKey !== data.newPKeyConfirm) {
    //   showToast.error('密碼與確認密碼不符');
    //   return;
    // }

    // // 如果是編輯模式且沒有輸入密碼，從提交數據中移除密碼相關欄位
    // if (mode === 'edit' && !data.newPKey.trim()) {
    //   delete data.newPKey;
    //   delete data.newPKeyConfirm;
    // }

    const trimmedPKey = data.newPKey?.trim();

    if (data?.role === "Member") {
      data.roleName = "Normal Member";
      data.testerSubscriptionEndAt = null;
    } else if (data?.role === "ExternalTester") {
      data.roleName = "External Tester";
    } else if (data?.role === "InternalTester") {
      data.roleName = "Internal Tester";
      data.testerSubscriptionEndAt = null;
    }

    if (data.testerSubscriptionEndAt) {
      const transferredDate = convertLocalToUTC(data.testerSubscriptionEndAt);
      data.testerSubscriptionEndAt = transferredDate;
    }

    try {
      let res;
      if (mode === 'edit') {
        res = await NewMemberAccountService.update(id, { ...data, newPKey: trimmedPKey, newPKeyConfirm: trimmedPKey });
        showToast.success('Account updated successfully.');
      } else {
        res = await NewMemberAccountService.create(data);
        showToast.success('Account created successfully.');
        setTimeout(() => {
          Navigate('/member/account');
        }, 1000);
      }
    } catch (error) {
      console.error('Form submission failed:', error);
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        logout();
      } else if (error?.response?.status === 400) {
        showToast.error(STATUS_MESSAGES[error?.response?.data?.errorCode] || 'Failed to save. Please try again later.');
      } else {
        showToast.error(error?.response?.data?.message || 'Failed to save. Please try again later.');
      }
    }
  };

  const roleValue = watch('role');
  const subscriptionLevelValue = watch('subscriptionLevel');

  useEffect(() => {
    if (roleValue !== "ExternalTester") {
      setValue("testerSubscriptionEndAt", null);
    }
  }, [roleValue])

  useEffect(() => {
    const currentRole = watch('role');
    
    // 如果是初次載入，只記錄當前角色，不進行任何操作
    if (isInitialLoadRef.current) {
      previousRoleRef.current = currentRole;
      isInitialLoadRef.current = false;
      return;
    }
    
    // 只有當用戶將角色從非 Member 改成 Member 時才執行
    if (previousRoleRef.current !== 'Member' && currentRole === 'Member') {
      setValue('subscriptionLevel', 'free');
    }
    
    // 更新上一次的角色記錄
    previousRoleRef.current = currentRole;
  }, [watch('role'), setValue]);

  return (
    <div className="container mx-auto p-6">
      <div className='flex justify-between items-center'>
        <button onClick={() => Navigate(-1)} className="text-brandDefault group flex items-center w-fit p-3 -ml-3">
          <ChevronLeft size={36} className="text-brandDefault mr-1" />
          <span className="font-bold">Back</span>
        </button>
        {mode === "edit" && (
          <button
            onClick={() => setShowDeleteModal(true)}
            className="w-28 py-2 border border-brandDefault text-brandDefault rounded-xl"
            disabled={isSubmitting}
          >
            Delete
          </button>
        )}
      </div>

      <div className="mx-auto bg-white rounded-lg py-6">
        <div className='flex justify-between items-center'>
          <h1 className="text-2xl font-bold mb-6">
            {mode === 'edit' ? 'Edit Member ' : 'Create Member'}
          </h1>
          <div className='flex'>
            <button
              onClick={() => setShowCancelModal(true)}
              className="w-28 py-2 border border-brandDefault text-brandDefault rounded-xl mr-3"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="w-28 py-2 border border-brandDefault bg-brandDefault text-white rounded-xl"
              disabled={isSubmitting}
              onClick={() => handleSubmit(onSubmit)()}
            >
              {isSubmitting ? "Saving..." : "Save"}
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className='relative !mt-8'>
            <h3 className='text-blue-500 font-bold relative z-10'>Personal Information</h3>
            <span className='absolute bottom-0 bg-blue-100 w-[11rem] h-[1rem]'></span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 !-mt-4">
            {/* 姓名 */}
            <div className="mt-10 w-full flex">
              <div className='grow mr-4'>
                <label className="block text-sm font-medium">
                  First Name
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  {...register('firstName', { 
                    required: 'First name is required.',
                    validate: value => value?.trim() !== '' || 'First name is required.'
                  })}
                  className={`w-full p-2 border rounded ${errors.firstName ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.firstName && (
                  <p className="text-red-500 text-sm">{errors.firstName.message}</p>
                )}
              </div>

              <div className='grow'>
                <label className="block text-sm font-medium">
                  Last Name
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  {...register('lastName', { 
                    required: 'Last name is required.',
                    validate: value => value?.trim() !== '' || 'Last name is required.'
                  })}
                  className={`w-full p-2 border rounded ${errors.lastName ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.lastName && (
                  <p className="text-red-500 text-sm">{errors.lastName.message}</p>
                )}
              </div>
            </div>

            {/* 大頭照 */}
            <div 
              className={`w-[215px] h-[190px] flex items-center justify-center shrink-0 relative border border-gray-900 translate-y-1 mx-auto rounded z-10 ${!watch('profilePhoto') && !localPreview ? 'cursor-pointer' : ''} self-center -mt-2 -mb-20`}
            >
              {/* 顯示圖片的邏輯，優先使用本地預覽，然後是已保存的圖片 */}
              {localPreview ? (
                /* 上傳過程中顯示本地預覽 */
                <img 
                  src={localPreview} 
                  className="w-full h-full object-contain"
                  alt="預覽圖"
                />
              ) : watch('profilePhoto') ? (
                /* 使用已保存的圖片 */
                <img 
                  src={`${process.env.REACT_APP_FILE_BASIC}${watch('profilePhoto')}`} 
                  className="w-full h-full object-contain"
                  alt="個人檔案照片"
                />
              ) : (
                /* 沒有圖片時顯示上傳圖示 */
                <div className='flex flex-col items-center justify-center'>
                  <Upload size={40} className="mb-2 text-gray-900" />
                  <span className='text-center text-gray-900 text-xs'>Upload Image</span>
                </div>
              )}
              
              {/* 上傳中顯示的加載指示器 */}
              {uploading && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <div className="text-white text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto"></div>
                    <p className="mt-2 text-xs">處理中...</p>
                  </div>
                </div>
              )}
              
              {/* 上傳照片按鈕（隱藏的檔案輸入） */}
              <input
                id="photo-upload"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handlePhotoUpload}
                ref={photoInputRef}
                disabled={uploading}
              />
              
              {/* 按鈕區域 */}
              {(watch('profilePhoto') || localPreview) && !uploading ? (
                <div className='absolute top-0.5 end-0.5 flex items-center'>
                  <Pencil 
                    size={30} 
                    className='bg-black/75 text-white hover:bg-black/100 rounded-full p-1 cursor-pointer' 
                    onClick={() => photoInputRef.current.click()}
                    title="重新上傳照片"
                  />
                  {/* <Trash2 
                    size={30} 
                    className='bg-red-500 text-white hover:bg-red-600 ml-0.5 rounded-full p-1 cursor-pointer' 
                    onClick={handleDeletePhoto}
                    title="刪除照片"
                  /> */}
                </div>
              ) : !uploading ? (
                // 如果沒有照片且不在上傳中，整個區域可點擊觸發上傳
                <div 
                  className="absolute inset-0 cursor-pointer" 
                  onClick={() => photoInputRef.current.click()}
                  title="上傳照片"
                />
              ) : null}
            </div>

            {/* Email */}
            <div className="space-y-1 flex">
              <div className='w-full'>
                <label className="block text-sm font-medium">
                  Email
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <div className='flex'>
                  <input
                    {...register('email', {
                      required: 'Email is required.',
                      pattern: {
                        value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                        message: 'Please enter a valid email address.'
                      }
                    })}
                    disabled={mode === 'edit'}
                    className={`w-full p-2 border rounded ${errors.email ? 'border-red-500' : 'border-gray-300'} ${mode === "edit" ? "bg-gray-50" : ""}`}
                  />
                </div>
                {errors.email && (
                  <p className="text-red-500 text-sm">{errors.email.message}</p>
                )}
              </div>
            </div>
            
            {/* Empty */}
            <div className="space-y-1 flex"></div>

            {/* 職稱 */}
            <div className="space-y-1">
              <label className="block text-sm font-medium">
                Job Title = Role
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                {...register('jobTitle', { 
                  required: 'Job title is required.',
                  validate: value => value?.trim() !== '' || 'Job title is required.'
                })}
                className={`w-full p-2 border rounded ${errors.jobTitle ? 'border-red-500' : 'border-gray-300'}`}
              />
              {errors.jobTitle && (
                <p className="text-red-500 text-sm">{errors.jobTitle.message}</p>
              )}
            </div>

            {/* 公司 */}
            <div className="space-y-1">
              <label className="block text-sm font-medium">
                Company
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                {...register('company', { 
                  required: 'Company is required.',
                  validate: value => value?.trim() !== '' || 'Company is required.'
                })}
                className={`w-full p-2 border rounded ${errors.company ? 'border-red-500' : 'border-gray-300'}`}
              />
              {errors.company && (
                <p className="text-red-500 text-sm">{errors.company.message}</p>
              )}
            </div>
            
            <div className="mt-1 w-full flex">
              {/* 手機號碼 */}
              <div className="grow mr-4">
                <label className="block text-sm font-medium">
                  Phone Number (Mobile)
                  {/* <span className="text-red-500 ml-1">*</span> */}
                </label>
                <input
                  {...register('mobileNumber', { 
                    // required: '姓名為必填欄位'
                  })}
                  className={`w-full p-2 border rounded ${errors.mobileNumber ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.mobileNumber && (
                  <p className="text-red-500 text-sm">{errors.mobileNumber.message}</p>
                )}
              </div>

              {/* 辦公室電話 */}
              <div className="grow">
                <label className="block text-sm font-medium">
                  Phone Number (Office)
                  {/* <span className="text-red-500 ml-1">*</span> */}
                </label>
                <input
                  {...register('officePhoneNumber', { 
                    // required: '姓名為必填欄位'
                  })}
                  className={`w-full p-2 border rounded ${errors.officePhoneNumber ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.officePhoneNumber && (
                  <p className="text-red-500 text-sm">{errors.officePhoneNumber.message}</p>
                )}
              </div>
            </div>

            {/* 公司網站 */}
            <div className="space-y-1">
              <label className="block text-sm font-medium">
                Company Website
                {/* <span className="text-red-500 ml-1">*</span> */}
              </label>
              <input
                {...register('companyWebsite', { 
                  // required: '職稱為必填欄位'
                })}
                className={`w-full p-2 border rounded ${errors.companyWebsite ? 'border-red-500' : 'border-gray-300'}`}
              />
              {errors.companyWebsite && (
                <p className="text-red-500 text-sm">{errors.companyWebsite.message}</p>
              )}
            </div>

            <div className="mt-1 w-full flex">
              {/* 洲別 */}
              <div className="grow mr-4">
                <label className="block text-sm font-medium">
                  User's Global / Region
                  {/* <span className="text-red-500 ml-1">*</span> */}
                </label>
                <input
                  {...register('globalRegion', { 
                    // required: '姓名為必填欄位'
                  })}
                  className={`w-full p-2 border rounded ${errors.globalRegion ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.globalRegion && (
                  <p className="text-red-500 text-sm">{errors.globalRegion.message}</p>
                )}
              </div>
              {/* 國家 */}
              <div className="grow">
                <label className="block text-sm font-medium">
                  User's Country / Region
                  {/* <span className="text-red-500 ml-1">*</span> */}
                </label>
                <input
                  {...register('country', { 
                    // required: '姓名為必填欄位'
                  })}
                  className={`w-full p-2 border rounded ${errors.country ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.country && (
                  <p className="text-red-500 text-sm">{errors.country.message}</p>
                )}
              </div>
            </div>

            {/* Company Primary Field */}
            <div className="space-y-1">
              <label className="block text-sm font-medium">
                Company Primary Field
                {/* <span className="text-red-500 ml-1">*</span> */}
              </label>
              <input
                {...register('companyPrimaryField', { 
                  // required: '職稱為必填欄位'
                })}
                className={`w-full p-2 border rounded ${errors.companyPrimaryField ? 'border-red-500' : 'border-gray-300'}`}
              />
              {errors.companyPrimaryField && (
                <p className="text-red-500 text-sm">{errors.companyPrimaryField.message}</p>
              )}
            </div>

            <div className="mt-1 w-full flex">
              {/* LinkedIn */}
              <div className="grow mr-4">
              <label className="block text-sm font-medium">
                LinkedIn
                {/* <span className="text-red-500 ml-1">*</span> */}
              </label>
              <input
                {...register('linkedIn', { 
                  // required: '姓名為必填欄位'
                })}
                className={`w-full p-2 border rounded ${errors.linkedIn ? 'border-red-500' : 'border-gray-300'}`}
              />
              {errors.linkedIn && (
                <p className="text-red-500 text-sm">{errors.linkedIn.message}</p>
              )}
            </div>

            {/* socialMedia2 */}
            <div className="grow">
              <label className="block text-sm font-medium">
                Facebook
                {/* <span className="text-red-500 ml-1">*</span> */}
              </label>
              <input
                {...register('facebook', { 
                  // required: '姓名為必填欄位'
                })}
                className={`w-full p-2 border rounded ${errors.facebook ? 'border-red-500' : 'border-gray-300'}`}
              />
              {errors.facebook && (
                <p className="text-red-500 text-sm">{errors.facebook.message}</p>
              )}
            </div>
          </div>
        </div>

        <div className='relative !mt-10'>
            <h3 className='text-blue-500 font-bold relative z-10'>Persona</h3>
            <span className='absolute bottom-0 bg-blue-100 w-[5rem] h-[1rem]'></span>
          </div>

          <div className="grid grid-cols-1 gap-6">
            {/* Reason for Use */}
            <div className="space-y-1">
              <label className="block text-sm font-medium">
                Reason for Use
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="w-full p-2 border rounded border-gray-300 bg-gray-100 min-h-[2.5rem] flex flex-wrap gap-2">
                {watch('reasonForUse') && Array.isArray(watch('reasonForUse')) ? (
                  watch('reasonForUse').map((reason, index) => (
                    <span 
                      key={index} 
                      className="inline-block px-3 py-1 border border-gray-400 rounded-md text-sm"
                    >
                      {reason}
                    </span>
                  ))
                ) : (
                  <span className="text-gray-500">(No reasons selected)</span>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6">
            {/* Primary Interest */}
            <div className="space-y-1">
              <label className="block text-sm font-medium">
                Primary Interest
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="w-full p-2 border rounded border-gray-300 bg-gray-100 min-h-[2.5rem] flex flex-wrap gap-2">
                {watch('primaryInterest') && Array.isArray(watch('primaryInterest')) ? (
                  watch('primaryInterest').map((interest, index) => (
                    <span 
                      key={index} 
                      className="inline-block px-3 py-1 border border-gray-400 rounded-md text-sm"
                    >
                      {interest}
                    </span>
                  ))
                ) : (
                  <span className="text-gray-500">(No interests selected)</span>
                )}
              </div>
            </div>

            {/* Detail Interest */}
            <div className="space-y-1">
              <label className="block text-sm font-medium">
                Detail Interest
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="w-full p-2 border rounded border-gray-300 bg-gray-100 min-h-[2.5rem] flex flex-wrap gap-2">
                {watch('detailInterest') && Array.isArray(watch('detailInterest')) ? (
                  watch('detailInterest').map((interest, index) => (
                    <span 
                      key={index} 
                      className="inline-block px-3 py-1 border border-gray-400 rounded-md text-sm"
                    >
                      {interest}
                    </span>
                  ))
                ) : (
                  <span className="text-gray-500">(No interests selected)</span>
                )}
              </div>
            </div>
          </div>

          <div className='relative !mt-10'>
            <h3 className='text-blue-500 font-bold relative z-10'>Role & Subscription</h3>
            <span className='absolute bottom-0 bg-blue-100 w-[10rem] h-[1rem]'></span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* 角色 */}
            <div className="space-y-1">
              <label className="block text-sm font-medium">
                User Role
                <span className="text-red-500 ml-1">*</span>
              </label>
              <select
                {...register('role', { 
                  required: 'User role is required.'
                })}
                value={watch('role') || 'Member'}
                className={`w-full p-2 border rounded ${errors.role ? 'border-red-500' : 'border-gray-300'}`}
                disabled={isRoleDisabled}
              >
                {/* <option value="">請選擇角色</option> */}
                {roleOptions.map((option, index) => (
                  <option key={option.name || index} value={option.name}>
                    {option.displayName}
                  </option>
                ))}
              </select>
              {errors.role && (
                <p className="text-red-500 text-sm">{errors.role.message}</p>
              )}
            </div>

            {/* Tester Subscription End Date */}
            <div className="space-y-1">
              <label className="block text-sm font-medium">
                Tester Subscription End At
                {watch('role') === 'ExternalTester' && <span className="text-red-500 ml-1">*</span>}
              </label>
              <input
                type="datetime-local"
                {...register('testerSubscriptionEndAt', {
                  required: watch('role') === 'ExternalTester' ? 'Tester subscription end date and time is required for External Tester.' : false,
                  validate: (value) => {
                    if (watch('role') === 'ExternalTester') {
                      if (!value) {
                        return 'Tester subscription end date and time is required for External Tester.';
                      }
                    }
                    return true;
                  }
                })}
                value={watch('testerSubscriptionEndAt') || ''}
                disabled={watch('role') !== 'ExternalTester'}
                className={`w-full px-2 py-1.5 border rounded ${
                  errors.testerSubscriptionEndAt ? 'border-red-500' : 'border-gray-300'
                } ${watch('role') !== 'ExternalTester' ? 'bg-gray-100' : ''}`}
              />
              {errors.testerSubscriptionEndAt && (
                <p className="text-red-500 text-sm">{errors.testerSubscriptionEndAt.message}</p>
              )}
            </div>

            {/* 訂閱等級 */}
            <div className="space-y-1">
              <label className="block text-sm font-medium">
                Current Plan
                <span className="text-red-500 ml-1">*</span>
              </label>
              <select
                {...register('subscriptionLevel', { 
                  required: 'Current plan is required.'
                })}
                value={watch('subscriptionLevel') || "free"}
                className={`w-full p-2 border rounded ${errors.subscriptionLevel ? 'border-red-500' : 'border-gray-300'}`}
                disabled={watch('role') === "Member"}
              >
                {/* <option value="">請選擇訂閱方案</option> */}
                {subscriptionLevelOptions.map((option, index) => (
                  <option key={option.name || index} value={option.name}>
                    {option.displayName}
                  </option>
                ))}
              </select>
              {errors.subscriptionLevel && (
                <p className="text-red-500 text-sm">{errors.subscriptionLevel.message}</p>
              )}
            </div>

            {/* Empty */}
            <div className="space-y-1"></div>

            {/* 是否訂閱 */}
            <div className="space-y-1">
              <label className="block text-sm font-medium">
                Payment Status
                {/* <span className="text-red-500 ml-1">*</span> */}
              </label>
              <p>{watch('paymentStatus') || "(No data)"}</p>
              {/* <select
                {...register('paymentStatus', { 
                  required: 'Payment Status is required.'
                })}
                value={watch('paymentStatus') || "notSubscribed"}
                className={`w-full p-2 border rounded ${errors.paymentStatus ? 'border-red-500' : 'border-gray-300'}`}
              >
                {paymentStatusOptions.map((option, index) => (
                  <option key={option.name || index} value={option.name}>
                    {option.displayName}
                  </option>
                ))}
              </select>
              {errors.paymentStatus && (
                <p className="text-red-500 text-sm">{errors.paymentStatus.message}</p>
              )} */}
            </div>

            {/* Empty */}
            <div className="space-y-1"></div>

            {/* 付款日期 */}
            <div className="space-y-1">
              <label className="block text-sm font-medium">
                Payment Date
                {/* <span className="text-red-500 ml-1">*</span> */}
              </label>
              <div className='relative flex items-center'>
                <p>{watch('paymentDate') ? datetimeFormatter(watch('paymentDate'))?.replaceAll("/", "-")?.substring(0, 10) : "(No data)"}</p>
                {/* <input
                  {...register('paymentDate', { 
                  })}
                  value={watch('paymentDate') ? datetimeFormatter(watch('paymentDate'))?.replaceAll("/", "-")?.substring(0, 10) : ""}
                  type='date'
                  className={`w-full p-2 border rounded ${errors.paymentDate ? 'border-red-500' : 'border-gray-300'}`}
                /> */}
              </div>
              {errors.paymentDate && (
                <p className="text-red-500 text-sm">{errors.paymentDate.message}</p>
              )}
            </div>
          </div>

          <div className='relative !mt-10'>
            <h3 className='text-blue-500 font-bold relative z-10'>Account Management</h3>
            <span className='absolute bottom-0 bg-blue-100 w-[11rem] h-[1rem]'></span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* 密碼 */}
            {/* {mode === "create" && ( */}
              <div className="space-y-1">
                <label className="block text-sm font-medium">
                  Password
                  {/* <span className="text-red-500 ml-1">*</span> */}
                </label>
                <div className="relative">
                  <input 
                    type={showPassword ? "password" : "text"}
                    {...register('newPKey', {
                      pattern: {
                        value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{6,}$/,
                        message: 'Password must be at least 6 characters and contain at least one uppercase letter, one lowercase letter, and one number.'
                      }
                    })}
                    placeholder='********'
                    autoComplete='new-password'
                    className="w-full p-2 border rounded border-gray-300"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {errors.newPKey && (
                  <p className="text-red-500 text-sm">{errors.newPKey.message}</p>
                )}
              </div>
              {/* )} */}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="mt-1 w-full flex">
              {/* Activation Status */}
              <div className="grow mr-4">
                <label className="block text-sm font-medium">
                  Activation Status
                </label>
                <div className="flex items-center gap-2">{watch('accountStatus') || '(No data)'}</div>
              </div>

              {/* Enable */}
              <div className="grow">
                <label className="block text-sm font-medium">
                  Active
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setShowIsEnabledModal(true)}
                    className={`relative inline-flex h-8 w-14 items-center rounded-full 
                      ${watch('isEnabled') ? 'bg-activationBtnGreen' : 'bg-activationBtnGray'}
                    `}
                  >
                    <span
                      className={`inline-block h-7 w-7 transform rounded-full transition-transform duration-300 bg-white ${
                        watch('isEnabled') ? 'translate-x-[1.6rem]' : 'translate-x-[0.1rem]'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className='relative !mt-10'>
            <h3 className='text-blue-500 font-bold relative z-10'>Admin Notes</h3>
            <span className='absolute bottom-0 bg-blue-100 w-[7.5rem] h-[1rem]'></span>
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-medium">Notes</label>
            <textarea
              {...register('adminNotes')}
              rows={4}
              placeholder="Add internal notes about this member..."
              className="w-full p-2 border rounded border-gray-300 text-sm resize-y"
            />
          </div>

          <div className="flex justify-end mt-8">
            <button
              type="submit"
              className="w-28 py-2 bg-brandDefault text-white rounded-xl"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        setIsOpen={setShowDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title=""
        message="Are you sure you want to delete this member?"
        cancelBtn="Cancel"
        confirmBtn="Delete"
      />

      <ConfirmationModal
        isOpen={showCancelModal}
        setIsOpen={setShowCancelModal}
        onClose={() => Navigate('/member/account')}
        onConfirm={() => setShowCancelModal(false)}
        title=""
        message="Do you want to keep editing?"
        cancelBtn="No"
        confirmBtn="Keep Editing"
      />

      <ConfirmationModal
        isOpen={showIsEnabledModal}
        setIsOpen={setShowIsEnabledModal}
        onClose={() => setShowIsEnabledModal(false)}
        onConfirm={handleToggleIsEnabled}
        title=""
        message={`Are you sure you want to ${watch("isEnabled") ? "inactive" : "active"} this member?`}
        cancelBtn="Cancel"
        confirmBtn={watch("isEnabled") ? "Inactive" : "Active"}
      />
    </div>
  );
}

export default MemberAccountForm;